import { ErrorState, LoadingState } from '@playforge/ui';
import { Badge, BlockStack, Button, Card, InlineGrid, InlineStack, Page, Text } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import { GAME_EMOJI } from '../components/GamePreview';
import type { GameTypeId, GameTypeOption, GameView } from '../lib/types';

const GAME_DESCRIPTIONS: Record<GameTypeId, string> = {
  spin_wheel: 'A classic prize wheel — customers spin and land on a reward segment.',
  scratch_card: 'Customers scratch a card to reveal the prize hiding underneath.',
  lucky_box: 'A grid of mystery boxes — pick one to reveal what is inside.',
  gift_box: 'A single gift box customers tap to open and claim their reward.',
  mystery_gift: 'A suspenseful mystery reveal — tap to uncover a surprise prize.',
};

export function GamesPage(): JSX.Element {
  const navigate = useNavigate();
  const types = useApi(() => apiClient.get<GameTypeOption[]>('/games/types'), []);
  const games = useApi(() => apiClient.get<GameView[]>('/games'), []);

  const usageByType = (games.data ?? []).reduce<Record<string, number>>((acc, g) => {
    acc[g.type] = (acc[g.type] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <Page
      title="Games"
      subtitle="The interactive games you can attach to a campaign"
      primaryAction={{ content: 'Go to campaigns', onAction: () => navigate('/campaigns') }}
    >
      {types.loading ? <LoadingState label="Loading games" /> : null}
      {types.error ? <ErrorState message={types.error.message} onRetry={types.refetch} /> : null}

      {types.data ? (
        <BlockStack gap="400">
          <Card>
            <Text as="p" tone="subdued">
              Each campaign runs one game. Pick a game type in the campaign builder&apos;s{' '}
              <b>Game</b> tab, then customize its look under <b>Themes</b>.
            </Text>
          </Card>

          <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="400">
            {types.data.map((t) => {
              const id = t.type as GameTypeId;
              const used = usageByType[t.type] ?? 0;
              return (
                <Card key={t.type}>
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                      <div
                        style={{
                          fontSize: 40,
                          width: 64,
                          height: 64,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 16,
                          background: 'linear-gradient(135deg, #eef2ff, #f5f3ff)',
                        }}
                      >
                        {GAME_EMOJI[id] ?? '🎮'}
                      </div>
                      {used > 0 ? <Badge tone="success">{`In use (${used})`}</Badge> : <Badge>Available</Badge>}
                    </InlineStack>
                    <Text as="h3" variant="headingMd">
                      {t.name}
                    </Text>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {GAME_DESCRIPTIONS[id] ?? ''}
                    </Text>
                    <InlineStack align="start">
                      <Button onClick={() => navigate('/themes')}>Preview &amp; theme</Button>
                    </InlineStack>
                  </BlockStack>
                </Card>
              );
            })}
          </InlineGrid>
        </BlockStack>
      ) : null}
    </Page>
  );
}
